/* eslint-disable @typescript-eslint/no-non-null-assertion */
import React, { useCallback, useMemo } from 'react'
import { Gallery, OrganisationInfo, PlainText } from 'shared'
import { Modal, TrustedHtml, findApiV4Link } from 'shared'
import { ModalContentProps, ProjectModalProps } from './types'
import { PPPProfilePicture } from 'ppp/types'
import { Progress } from '../progress'

export const ProjectModal = ({ hideModal, showPreviewModal, project, projectSelectionUrl }: ProjectModalProps) => {
  const handleSelectClick = useCallback(
    (event: React.MouseEvent, projectId: number) => {
      const selectionUrl = projectSelectionUrl.replace('PROJECT_ID', `${projectId}`)

      event.preventDefault()
      window.open(selectionUrl, event.metaKey ? undefined : '_self')
    },
    [projectSelectionUrl]
  )

  return (
    <Modal
      id="project-preview-modal"
      onHide={hideModal}
      show={showPreviewModal}
      header={<PlainText>{project.title || ''}</PlainText>}
    >
      <ModalContent project={project} />
      <div className="flex items-center justify-end">
        <button className="simulated-link mr-8" type="button" onClick={hideModal}>
          {i18n.t('misc.close')}
        </button>
        <button className="btn btn-primary" onClick={(event) => handleSelectClick(event, project.id)}>
          {i18n.t('fundraising_events.preselections.show.cta_button')}
        </button>
      </div>
    </Modal>
  )
}

const useGalleryProfilePicture = (
  profilePicture: ModalContentProps['project']['profile_picture']
): PPPProfilePicture => {
  const picture = useMemo(() => {
    //All projects in the modal should have a profile picture
    /** Use overdefined image sizes (gallery will be rendered smaller) */
    const desktop1xUrl = findApiV4Link({
      ofObject: profilePicture,
      withRel: 'fill_618x322',
    })!
    const desktop2xUrl = findApiV4Link({
      ofObject: profilePicture, //All projects in the modal should have a profile picture
      withRel: 'limit_1240x646',
    })!
    const mobile1xUrl = findApiV4Link({
      ofObject: profilePicture, //All projects in the modal should have a profile picture
      withRel: 'limit_450x235',
    })!
    const mobile2xUrl = findApiV4Link({
      ofObject: profilePicture, //All projects in the modal should have a profile picture
      withRel: 'fill_960x500',
    })!
    return {
      mobile1xUrl,
      mobile2xUrl,
      desktop1xUrl,
      desktop2xUrl,
    }
  }, [profilePicture])
  return picture
}

const ModalContent = ({ project }: ModalContentProps) => {
  const profilePictureUrl = project.carrier
    ? findApiV4Link({
        ofObject: project.carrier.picture!,
        withRel: 'fill_100x100',
      })
    : ''
  const profilePicture = useGalleryProfilePicture(project.profile_picture)
  return (
    <div>
      {profilePicture && (
        <Gallery
          profilePicture={profilePicture}
          apiUrl={
            findApiV4Link({
              ofObject: project,
              withRel: 'pictures',
            }) || ''
          }
        />
      )}
      <div className="flex items-center mt-6">
        {project.carrier && (
          <OrganisationInfo
            carrierProfilePictureUrl={profilePictureUrl}
            carrierName={project.carrier.name}
            city={project.city}
            country={project.country}
          />
        )}
      </div>

      <Progress project={project} status="activated" />
      <TrustedHtml as="p" className="my-6">
        {project.summary}
      </TrustedHtml>
    </div>
  )
}
